import { Autocomplete as MUIAutocomplete, Grid, Paper, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useFormikContext } from 'formik';
import { useRef, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { ICareTeam } from '../../Models/CareTeams/CareTeam.model';
import { useGoogleMaps } from '../../hooks/GoogleMapsContext';
import { states } from '../../utils/constants';

export const AddressValidationSchema = Yup.object().shape({
    Address1: Yup.string().required('Address Line 1 is required'),
    Address2: Yup.string(),
    City: Yup.string().required('City is required'),
    State: Yup.string().required('State is required'),
    ZipCode: Yup.string()
        .required('Zip Code is required')
        .matches(/^\d{5}(-\d{4})?$/, 'Zip Code must be in the format 12345 or 12345-6789'),
});

const TeamAddressForm = () => {
    const { loaded } = useGoogleMaps();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const autocompleteServiceRef = useRef<any>(null);
    const placesServiceRef = useRef<any>(null);
    const debounceTimer = useRef<number | null>(null);
    const [predictions, setPredictions] = useState<Array<{ description: string; place_id: string }>>([]);

    const { handleBlur, touched, errors, setFieldValue } = useFormikContext<ICareTeam>();
    const { values } = useFormikContext<ICareTeam>();
    const [inputValueLocal, setInputValueLocal] = useState(values.MeetingAddress.Address1 || '');

    // keep local input in sync with formik value
    useEffect(() => {
        setInputValueLocal(values.MeetingAddress.Address1 || '');
    }, [values.MeetingAddress.Address1]);

    // Initialize Google services when loaded; poll briefly if library attachment is delayed
    useEffect(() => {
        if (!loaded) return;

        const initServices = () => {
            autocompleteServiceRef.current = new (window as any).google.maps.places.AutocompleteService();
            const dummyDiv = document.createElement('div');
            placesServiceRef.current = new (window as any).google.maps.places.PlacesService(dummyDiv);
        };

        if ((window as any).google && (window as any).google.maps && (window as any).google.maps.places) {
            initServices();
            return () => {
                autocompleteServiceRef.current = null;
                placesServiceRef.current = null;
                setPredictions([]);
                if (debounceTimer.current) {
                    clearTimeout(debounceTimer.current);
                    debounceTimer.current = null;
                }
            };
        }

        let pollId: number | undefined;
        const startPoll = () => {
            pollId = window.setInterval(() => {
                if ((window as any).google && (window as any).google.maps && (window as any).google.maps.places) {
                    initServices();
                    if (pollId) {
                        clearInterval(pollId);
                        pollId = undefined;
                    }
                }
            }, 100);
            window.setTimeout(() => {
                if (pollId) {
                    clearInterval(pollId);
                    pollId = undefined;
                }
            }, 5000);
        };

        startPoll();

        return () => {
            if (pollId) {
                clearInterval(pollId);
            }
            autocompleteServiceRef.current = null;
            placesServiceRef.current = null;
            setPredictions([]);
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
                debounceTimer.current = null;
            }
        };
    }, [loaded]);

    const fetchPredictions = (input: string) => {
        if (!input) {
            setPredictions([]);
            return;
        }

        // lazy init if possible
        if (!autocompleteServiceRef.current) {
            if ((window as any).google && (window as any).google.maps && (window as any).google.maps.places) {
                try {
                    autocompleteServiceRef.current = new (window as any).google.maps.places.AutocompleteService();
                } catch (err) {
                    // ignore
                }
            }
        }

        if (!autocompleteServiceRef.current) {
            setPredictions([]);
            return;
        }

        autocompleteServiceRef.current.getPlacePredictions({ input, types: ['address'] }, (preds: any) => {
            if (!preds) {
                setPredictions([]);
                return;
            }
            setPredictions(preds.map((p: any) => ({ description: p.description, place_id: p.place_id })));
        });
    };

    const onInputChange = (value: string) => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = window.setTimeout(() => {
            fetchPredictions(value);
        }, 250);
    };

    const onSelectPrediction = (placeId: string | null, description?: string) => {
        if (!placeId) return;

        if (!placesServiceRef.current) {
            if ((window as any).google && (window as any).google.maps && (window as any).google.maps.places) {
                try {
                    const dummyDiv = document.createElement('div');
                    placesServiceRef.current = new (window as any).google.maps.places.PlacesService(dummyDiv);
                } catch (err) {
                    // ignore
                }
            }
        }

        if (!placesServiceRef.current) return;

        placesServiceRef.current.getDetails({ placeId }, (placeResult: any, status: string) => {
            if (status !== (window as any).google.maps.places.PlacesServiceStatus.OK || !placeResult) return;
            if (!placeResult.address_components) return;
            const addressComponents = placeResult.address_components.reduce(
                (acc: any, component: any) => {
                    if (component.types.includes('street_number')) {
                        acc.Address1 = component.long_name;
                    } else if (component.types.includes('route')) {
                        acc.Address1 += ' ' + component.long_name;
                    } else if (component.types.includes('locality')) {
                        acc.City = component.long_name;
                    } else if (component.types.includes('administrative_area_level_1')) {
                        acc.State = component.short_name;
                    } else if (component.types.includes('postal_code')) {
                        acc.ZipCode = component.long_name;
                    }
                    return acc;
                },
                { Address1: '', City: '', State: '', ZipCode: '' }
            );
            setFieldValue('MeetingAddress', addressComponents);
            setTimeout(() => setPredictions([]), 0);
        });
    };

    return (
        <Grid size={12}>
            <Paper variant="outlined" sx={{ padding: 2, position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: -12, left: 16, backgroundColor: 'white', padding: '0 8px' }}>
                    <Typography variant="subtitle1">Team Address</Typography>
                </Box>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        {loaded ? (
                            <MUIAutocomplete
                                freeSolo
                                options={predictions.map((p) => p.description)}
                                filterOptions={(options) => options}
                                onInputChange={(_, newValue) => {
                                    setInputValueLocal(newValue || '');
                                    setFieldValue('MeetingAddress.Address1', newValue || '');
                                    if (!newValue) setPredictions([]);
                                    onInputChange(newValue || '');
                                }}
                                onChange={(_, value) => {
                                    const pred = predictions.find((p) => p.description === value);
                                    if (pred) {
                                        onSelectPrediction(pred.place_id, pred.description);
                                        setFieldValue('MeetingAddress.Address1', pred.description);
                                        setPredictions([]);
                                    } else if (typeof value === 'string') {
                                        setFieldValue('MeetingAddress.Address1', value);
                                        setPredictions([]);
                                    }
                                }}
                                inputValue={inputValueLocal}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        inputRef={inputRef}
                                        label="Address"
                                        placeholder="Start typing an address..."
                                        name="MeetingAddress.Address1"
                                        onBlur={handleBlur}
                                        error={touched.MeetingAddress?.Address1 && Boolean(errors.MeetingAddress?.Address1)}
                                        helperText={touched.MeetingAddress?.Address1 && errors.MeetingAddress?.Address1}
                                    />
                                )}
                            />
                        ) : (
                            <TextField
                                fullWidth
                                label="Address"
                                placeholder="Start typing an address..."
                                name="MeetingAddress.Address1"
                                value={values.MeetingAddress.Address1}
                                onChange={(e) => setFieldValue('MeetingAddress.Address1', e.target.value)}
                                onBlur={handleBlur}
                                error={touched.MeetingAddress?.Address1 && Boolean(errors.MeetingAddress?.Address1)}
                                helperText={touched.MeetingAddress?.Address1 && errors.MeetingAddress?.Address1}
                            />
                        )}
                    </Grid>
                    <Grid size={12}>
                        <TextField
                            fullWidth
                            label="Address Line 2"
                            name="MeetingAddress.Address2"
                            value={values.MeetingAddress.Address2}
                            onChange={(e) => setFieldValue('MeetingAddress.Address2', e.target.value)}
                            onBlur={handleBlur}
                            error={touched.MeetingAddress?.Address2 && Boolean(errors.MeetingAddress?.Address2)}
                            helperText={touched.MeetingAddress?.Address2 && errors.MeetingAddress?.Address2}
                        />
                    </Grid>
                    <Grid size={6}>
                        <TextField
                            fullWidth
                            label="City"
                            name="MeetingAddress.City"
                            value={values.MeetingAddress.City}
                            onChange={(e) => setFieldValue('MeetingAddress.City', e.target.value)}
                            onBlur={handleBlur}
                            error={touched.MeetingAddress?.City && Boolean(errors.MeetingAddress?.City)}
                            helperText={touched.MeetingAddress?.City && errors.MeetingAddress?.City}
                        />
                    </Grid>
                    <Grid size={3}>
                        <MUIAutocomplete
                            options={states}
                            getOptionLabel={(option) => option}
                            value={values.MeetingAddress.State ?? ''}
                            onChange={(event, newValue) => {
                                setFieldValue('MeetingAddress.State', newValue ?? '');
                            }}
                            onBlur={() => handleBlur({ target: { name: 'MeetingAddress.State' } })}
                            isOptionEqualToValue={(option, value) => option === value}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="State"
                                    name="MeetingAddress.State"
                                    error={touched.MeetingAddress?.State && Boolean(errors.MeetingAddress?.State)}
                                    helperText={touched.MeetingAddress?.State && errors.MeetingAddress?.State}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={3}>
                        <TextField
                            fullWidth
                            label="Zip Code"
                            name="MeetingAddress.ZipCode"
                            value={values.MeetingAddress.ZipCode}
                            onChange={(e) => setFieldValue('MeetingAddress.ZipCode', e.target.value)}
                            onBlur={handleBlur}
                            error={touched.MeetingAddress?.ZipCode && Boolean(errors.MeetingAddress?.ZipCode)}
                            helperText={touched.MeetingAddress?.ZipCode && errors.MeetingAddress?.ZipCode}
                        />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default TeamAddressForm;
